import { cn } from "@/lib/utils";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { FC, useState } from "react";
import { ActivityLogListItem } from "@/app/ActivityLogs/GraphQL/activityLogListQuery";
import { Dialog } from "@/components/catalyst/dialog";
import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";
import dayjs from "dayjs";
import { useTranslations } from "use-intl";
import { getUserDisplayName } from "@/lib/Utils/getUserDisplayName";

export function DefaultActivityLogItem({
  log,
  Icon,
  iconColor = "text-gray-400",
}: {
  log: ActivityLogListItem;
  Icon?: FC<any>;
  iconColor?: string;
}) {
  const [open, setOpen] = useState(false);
  const t = useTranslations("activityLog");
  const hasDiff = !!(
    log.attribute_changes?.old || log.attribute_changes?.attributes
  );

  const content = (
    <div className="flex bg-transparent">
      <div className="relative z-10 mr-4 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
        {Icon ? (
          <Icon className={cn("size-5", iconColor)} aria-hidden="true" />
        ) : (
          <div className="h-1.5 w-1.5 rounded-full bg-gray-100 ring-1 ring-gray-300" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-900">
            {getUserDisplayName(log.causer, t("notation.guest"))}
          </span>
          <time
            className="shrink-0 text-xs text-gray-500"
            title={`${dayjs.tz.guess()}: ${formatDateTimeLocal(log.created_at)}`}
          >
            {formatDateTimeSinceNow(log.created_at)}
          </time>
        </div>
        {log.description ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
            {log.description}
          </p>
        ) : null}
      </div>
    </div>
  );

  if (hasDiff) {
    return (
      <>
        <button
          type="button"
          className="group block w-full rounded-md text-left transition-colors hover:bg-indigo-50/60"
          onClick={() => setOpen(true)}
        >
          {content}
        </button>
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          size="full"
          className="p-0"
        >
          <ReactDiffViewer
            oldValue={JSON.stringify(log.attribute_changes?.old, null, 2)}
            newValue={JSON.stringify(
              log.attribute_changes?.attributes,
              null,
              2,
            )}
            compareMethod={DiffMethod.WORDS}
            leftTitle={t("diff.old")}
            rightTitle={t("diff.new")}
          />
        </Dialog>
      </>
    );
  }

  return content;
}
